import React from "react";
import { AlertCircle } from "react-feather";
import { formatUtcDateTime } from "../utils/dateTime";

// Shared KnowBe4 diagnostic display pieces: the KnowBe4 card in Integration Settings and the Super
// Admin page's KnowBe4 diagnostics panel render the same last-sync banner and test result.

// KnowBe4 sync-state endpoint keys → the labels shown for an incomplete listing.
export const KNOWBE4_ENDPOINT_LABELS = [["phishing", "Phishing tests"], ["users", "Users"], ["enrollments", "Training enrollments"]];

/**
 * Last-sync banner: no state yet → blue "next refresh"; success → green with counts; partial →
 * amber with one line per incomplete endpoint; any other reason → red with the server message.
 */
export function KnowBe4SyncStatus({ region, lastSyncAt, syncState: state }) {
  if (!state) return (
    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800" data-testid="knowbe4-sync-status">
      <span>KnowBe4 is enabled — region <strong>{region || "us"}.api.knowbe4.com</strong>. Signals are pulled on the next refresh.</span>
    </div>
  );
  const time = formatUtcDateTime(lastSyncAt);
  if (state.success) return (
    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800" data-testid="knowbe4-sync-status">
      <span>✅</span>
      <span>Last synced {time}: {state.users ?? 0} users, {state.securityTests ?? 0} phishing tests, {state.enrollments ?? 0} training enrollments.</span>
    </div>
  );
  if (state.reason === "partial") {
    const incomplete = KNOWBE4_ENDPOINT_LABELS.filter(([key]) => state.endpoints?.[key] && !state.endpoints[key].complete);
    return (
      <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="knowbe4-sync-status">
        <div>Last sync {time} was incomplete. {state.message}</div>
        {incomplete.map(([key, label]) => (
          <div key={key} className="text-[11px] mt-0.5">
            {state.endpoints[key].skipped ? `${label}: skipped (time limit reached)` : `${label}: ${state.endpoints[key].reason}`}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800" data-testid="knowbe4-sync-status">
      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
      <span>Last sync {time} failed. {state.message}</span>
    </div>
  );
}

/**
 * A Test Connection result: { error: true } (the request itself failed) or a KnowBe4TestResult
 * (message + one ✓/✗ line per endpoint request).
 */
export function KnowBe4TestResult({ result }) {
  if (result.error) return (
    <div data-testid="knowbe4-test-result" className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
      Couldn't run the test. Try again.
    </div>
  );
  return (
    <div data-testid="knowbe4-test-result"
      className={`px-3 py-2 rounded-lg text-xs border ${result.success
        ? "bg-green-50 border-green-200 text-green-800"
        : result.reason === "partial"
          ? "bg-amber-50 border-amber-200 text-amber-800"
          : "bg-red-50 border-red-200 text-red-800"}`}>
      <div>{result.message}</div>
      {(result.endpoints || []).map(ep => (
        <div key={ep.name} className="text-[11px] mt-0.5">
          {ep.ok ? `✓ ${ep.name}` : `✗ ${ep.name} (${ep.status ?? ep.reason})`}
        </div>
      ))}
    </div>
  );
}
